import { useEffect, useMemo, useRef, useState } from "react";
import type { Cloud } from "../formations";
import type { RenderMode } from "../sources";
import { buildCloud, type Source } from "../source";
import { colorizeCloud } from "../gradient";
import {
  type SceneLook, type Timeline, type TransitionKind,
  DEFAULT_LOOK, defaultScene, defaultTransition, layout, newSceneId, toJSON, fromJSON,
} from "../sequencer";
import { fmt } from "../util";

// a fun playable default so the sequencer isn't a blank canvas on first open
// (the text + heart scenes carry looks, showing off per-scene colour)
const START: Timeline = {
  scenes: [
    defaultScene({ kind: "formation", key: "sphere" }, 2.5),
    defaultScene({ kind: "formation", key: "merkaba" }, 2.5),
    defaultScene({ kind: "text", text: "FREE.VET", font: "Arial" }, 3, { color: "solid", hex: "#ffffff" }),
    defaultScene({ kind: "formation", key: "heart" }, 2.5, { color: "solid", hex: "#ff2233" }),
  ],
  transitions: [
    { kind: "explode", duration: 1.6 },
    { kind: "spiral", duration: 1.8 },
    { kind: "implode", duration: 1.6 },
  ],
};

/**
 * Owns the show timeline: the scenes+transitions model, the transport (play /
 * scrub / loop), all editing ops, an async cloud cache (one cloud per scene at
 * the fixed fleet `count` — like a real show), and JSON save/load. The renderer
 * (SequencerField) reads `playhead` + `cloudsRef` every frame.
 */
export function useSequencer({
  count,
  renderMode,
  onError,
}: {
  count: number;
  renderMode: RenderMode;
  onError: (m: string) => void;
}) {
  const [timeline, setTimeline] = useState<Timeline>(START);
  const [playing, setPlaying] = useState(true);
  const [loop, setLoop] = useState(true);
  const [selected, setSelected] = useState<string | null>(START.scenes[0].id);
  const playhead = useRef(0);
  const [nowLabel, setNowLabel] = useState("0:00");

  const timing = useMemo(() => layout(timeline), [timeline]);

  // cloud cache: sceneId -> Cloud, coloured by the scene's look. Rebuild only
  // when SOURCES / LOOKS / count / render mode change (not hold/transition edits).
  const cloudsRef = useRef<Map<string, Cloud>>(new Map());
  const [cloudsVersion, setCloudsVersion] = useState(0);
  const sourcesKey = useMemo(
    () => timeline.scenes.map((s) => s.id + ":" + JSON.stringify(s.source) + ":" + JSON.stringify(s.look)).join("|"),
    [timeline.scenes]
  );
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map = new Map<string, Cloud>();
      for (const sc of timeline.scenes) {
        try {
          const c = await buildCloud(sc.source, count, renderMode);
          map.set(sc.id, { positions: c.positions, colors: colorizeCloud(c.positions, c.colors, sc.look) });
        } catch (e) {
          onError(e instanceof Error ? e.message : String(e));
        }
      }
      if (!cancelled) {
        cloudsRef.current = map;
        setCloudsVersion((v) => v + 1);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesKey, count, renderMode]);

  // playhead readout (the frame loop advances playhead; this just samples it)
  useEffect(() => {
    const id = window.setInterval(() => setNowLabel(fmt(playhead.current)), 150);
    return () => window.clearInterval(id);
  }, []);

  // ---------- editing (all immutable) ----------
  function addScene(source: Source, look: SceneLook = DEFAULT_LOOK) {
    const sc = defaultScene(source, 3, look);
    setTimeline((tl) => ({
      scenes: [...tl.scenes, sc],
      transitions: tl.scenes.length > 0 ? [...tl.transitions, defaultTransition()] : tl.transitions,
    }));
    setSelected(sc.id);
  }
  function removeScene(id: string) {
    setTimeline((tl) => {
      const i = tl.scenes.findIndex((s) => s.id === id);
      if (i < 0) return tl;
      const scenes = tl.scenes.filter((s) => s.id !== id);
      const transitions = tl.transitions.slice();
      transitions.splice(Math.min(i, transitions.length - 1), 1);
      return { scenes, transitions: transitions.slice(0, Math.max(0, scenes.length - 1)) };
    });
  }
  function duplicateScene(id: string) {
    setTimeline((tl) => {
      const i = tl.scenes.findIndex((s) => s.id === id);
      if (i < 0) return tl;
      const copy = { ...tl.scenes[i], id: newSceneId() };
      const scenes = [...tl.scenes.slice(0, i + 1), copy, ...tl.scenes.slice(i + 1)];
      const transitions = tl.transitions.slice();
      transitions.splice(i, 0, defaultTransition());
      return { scenes, transitions };
    });
  }
  function moveScene(id: string, dir: -1 | 1) {
    setTimeline((tl) => {
      const i = tl.scenes.findIndex((s) => s.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= tl.scenes.length) return tl;
      const scenes = tl.scenes.slice();
      [scenes[i], scenes[j]] = [scenes[j], scenes[i]];
      return { scenes, transitions: tl.transitions };
    });
  }
  function setSceneSource(id: string, source: Source, look?: SceneLook) {
    setTimeline((tl) => ({
      ...tl,
      scenes: tl.scenes.map((s) => (s.id === id ? { ...s, source, ...(look ? { look } : {}) } : s)),
    }));
  }
  function setSceneLook(id: string, look: SceneLook) {
    setTimeline((tl) => ({ ...tl, scenes: tl.scenes.map((s) => (s.id === id ? { ...s, look } : s)) }));
  }
  /** Drag-reorder: move scene `id` so it sits at `toIndex`. */
  function moveSceneTo(id: string, toIndex: number) {
    setTimeline((tl) => {
      const i = tl.scenes.findIndex((s) => s.id === id);
      if (i < 0) return tl;
      const j = Math.max(0, Math.min(toIndex, tl.scenes.length - 1));
      if (i === j) return tl;
      const scenes = tl.scenes.slice();
      const [moved] = scenes.splice(i, 1);
      scenes.splice(j, 0, moved);
      return { scenes, transitions: tl.transitions };
    });
  }
  function setSceneHold(id: string, hold: number) {
    setTimeline((tl) => ({ ...tl, scenes: tl.scenes.map((s) => (s.id === id ? { ...s, hold } : s)) }));
  }
  function setTransitionKind(i: number, kind: TransitionKind) {
    setTimeline((tl) => ({ ...tl, transitions: tl.transitions.map((t, k) => (k === i ? { ...t, kind } : t)) }));
  }
  function setTransitionDuration(i: number, duration: number) {
    setTimeline((tl) => ({ ...tl, transitions: tl.transitions.map((t, k) => (k === i ? { ...t, duration } : t)) }));
  }
  function seek(t: number) {
    playhead.current = Math.max(0, Math.min(t, timing.total));
  }

  // ---------- save / load ----------
  function save() {
    const blob = new Blob([toJSON(timeline)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "swarm-show.json";
    a.click();
    URL.revokeObjectURL(url);
  }
  function load(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f)
      f.text().then((t) => {
        try {
          const tl = fromJSON(t);
          setTimeline(tl);
          playhead.current = 0;
          setSelected(tl.scenes[0]?.id ?? null);
        } catch (err) {
          onError(err instanceof Error ? err.message : String(err));
        }
      });
    e.target.value = "";
  }

  return {
    timeline, timing, playing, setPlaying, loop, setLoop, playhead, nowLabel,
    selected, setSelected, cloudsRef, cloudsVersion,
    addScene, removeScene, duplicateScene, moveScene, moveSceneTo, setSceneSource, setSceneLook, setSceneHold,
    setTransitionKind, setTransitionDuration, seek, save, load,
  };
}
